// Таблицата със затворените сметки в избора за фактуриране (стъпка 1 от
// „Фактуриране" в Маркиране) — изнесена от `InvoiceBillSelectionModal` при
// режимите на фирмата (правилото за размера) БЕЗ промяна в маркъпа: чекбокс ·
// Час · Маса · Сервитьор (скрит на тесен) · Плащане · Сума. Изборът, търсенето
// и клавиатурата остават в модала; hover-ът на редовете е scoped CSS-ът там
// (`.inv-modal .row`).
import { Empty, Spin } from "antd";
import { CheckOutlined, MinusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { BillHistory } from "../../../../../api/reportsBillsHistory";
import { R } from "../../orderingTheme";
import { InvoiceBillAmount } from "./InvoiceBillAmount";

interface Props {
  isNarrow: boolean;
  isLoading: boolean;
  isError: boolean;
  /** Всички кандидати — „няма нефактурирани" срещу „няма по търсенето". */
  totalCount: number;
  filtered: BillHistory[];
  selected: ReadonlySet<number>;
  allSelected: boolean;
  someSelected: boolean;
  onToggleAll: () => void;
  onToggleOne: (id: number) => void;
  waiterName: (id: number | null) => string;
}

const formatTime = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("bg-BG", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const formatMoney = (v: number | string): string => {
  const n = typeof v === "number" ? v : parseFloat(v);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
};

export function InvoiceClosedBillsTable({
  isNarrow,
  isLoading,
  isError,
  totalCount,
  filtered,
  selected,
  allSelected,
  someSelected,
  onToggleAll,
  onToggleOne,
  waiterName,
}: Props) {
  const { t } = useTranslation();

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        padding: isNarrow ? "0 16px 12px" : "0 24px 14px",
        background: R.surface,
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${R.border}`,
          borderRadius: 10,
          overflow: "hidden",
          background: R.surface,
        }}
      >
        {/* Table header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isNarrow
              ? "32px 56px 1fr 1fr 90px"
              : "32px 70px 1fr 1.2fr 1.2fr 110px",
            gap: 10,
            padding: isNarrow ? "8px 12px" : "10px 14px",
            background: R.bg,
            borderBottom: `1px solid ${R.border}`,
            fontSize: 10.5,
            fontWeight: 700,
            color: R.textMuted,
            letterSpacing: 0.8,
            textTransform: "uppercase",
            alignItems: "center",
          }}
        >
          <CheckIndicator
            state={
              allSelected
                ? "checked"
                : someSelected
                  ? "indeterminate"
                  : "unchecked"
            }
            onClick={onToggleAll}
            ariaLabel={t("invoicing.selectAll")}
          />
          <span>{t("invoicing.colId")}</span>
          <span>{t("invoicing.colTable")}</span>
          {!isNarrow && <span>{t("invoicing.colWaiter")}</span>}
          <span>{t("invoicing.colPayment")}</span>
          <span style={{ textAlign: "right" }}>
            {t("invoicing.colTotal")}
          </span>
        </div>

        {/* Scrollable body */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            background: R.surface,
          }}
        >
      {isLoading ? (
        <div
          style={{
            padding: 48,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Spin />
        </div>
      ) : isError ? (
        <div style={{ padding: 48, textAlign: "center", color: R.danger }}>
          {t("invoicing.loadError")}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 32 }}>
          <Empty
            description={
              <span style={{ color: R.textSoft, fontSize: 13 }}>
                {totalCount === 0
                  ? t("invoicing.emptyAll")
                  : t("invoicing.emptyFiltered")}
              </span>
            }
          />
        </div>
      ) : (
        filtered.map((b) => {
          const isSel = selected.has(b.id);
          return (
            <div
              key={b.id}
              className={`row${isSel ? " is-selected" : ""}`}
              role="button"
              onClick={() => onToggleOne(b.id)}
              style={{
                display: "grid",
                gridTemplateColumns: isNarrow
                  ? "32px 56px 1fr 1fr 90px"
                  : "32px 70px 1fr 1.2fr 1.2fr 110px",
                gap: 10,
                padding: isNarrow ? "10px 12px" : "11px 14px",
                borderBottom: `1px solid ${R.border}`,
                alignItems: "center",
                cursor: "pointer",
                background: isSel ? R.brandTint : R.surface,
                fontSize: 13,
                color: R.ink,
              }}
            >
              <CheckIndicator
                state={isSel ? "checked" : "unchecked"}
                onClick={(e) => {
                  e?.stopPropagation();
                  onToggleOne(b.id);
                }}
                ariaLabel={`${t("invoicing.selectRow")} ${b.name}`}
              />
              <span
                style={{
                  fontFamily: R.mono,
                  fontSize: 12.5,
                  color: R.textSoft,
                }}
              >
                {formatTime(b.closed_at)}
              </span>
              <span style={{ fontWeight: 600 }}>{b.name}</span>
              {!isNarrow && (
                <span style={{ color: R.textSoft, fontSize: 12.5 }}>
                  {waiterName(b.waiter_id)}
                </span>
              )}
              <span style={{ color: R.textSoft, fontSize: 12.5 }}>
                {b.payment_method_name ?? "—"}
              </span>
              <span
                style={{
                  textAlign: "right",
                  fontFamily: R.mono,
                  fontWeight: 600,
                  color: R.ink,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                <InvoiceBillAmount bill={b} formatMoney={formatMoney} />
              </span>
            </div>
          );
        })
      )}
        </div>
      </div>
    </div>
  );
}

// Кръгъл „checkbox" в стила на CloseTableModal payment-method селектора —
// 20×20, brand-filled когато е избран, dash за indeterminate.
function CheckIndicator({
  state,
  onClick,
  ariaLabel,
}: {
  state: "checked" | "unchecked" | "indeterminate";
  onClick: (e?: React.MouseEvent) => void;
  ariaLabel: string;
}) {
  const filled = state === "checked" || state === "indeterminate";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      aria-checked={state === "checked"}
      role="checkbox"
      style={{
        width: 20,
        height: 20,
        borderRadius: 10,
        padding: 0,
        background: filled ? R.brand : R.surface,
        border: `1px solid ${filled ? R.brand : R.borderMed}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#FFFFFF",
        cursor: "pointer",
        flexShrink: 0,
        transition: "background 120ms, border-color 120ms",
      }}
    >
      {state === "checked" && <CheckOutlined style={{ fontSize: 11 }} />}
      {state === "indeterminate" && (
        <MinusOutlined style={{ fontSize: 11 }} />
      )}
    </button>
  );
}
